import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { FaUpRightAndDownLeftFromCenter } from "react-icons/fa6";
import { setManualBoardSize } from "@/redux/settings";
import { cn } from "@/lib/utils";

type ResizableBoardFrameProps = {
  children: ReactNode;
  className?: string;
  enabled?: boolean;
  size: number;
};

export default function ResizableBoardFrame({
  children,
  className,
  enabled = true,
  size,
}: ResizableBoardFrameProps) {
  const [isResizing, setIsResizing] = useState(false);
  const dragState = useRef<{
    startX: number;
    startY: number;
    startSize: number;
  } | null>(null);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!enabled || !size) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragState.current = {
        startX: event.clientX,
        startY: event.clientY,
        startSize: size,
      };

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "nwse-resize";
      document.body.style.userSelect = "none";
      setIsResizing(true);

      const resize = (moveEvent: PointerEvent) => {
        if (!dragState.current) return;

        const delta = Math.max(
          moveEvent.clientX - dragState.current.startX,
          moveEvent.clientY - dragState.current.startY,
        );
        setManualBoardSize(dragState.current.startSize + delta);
      };

      const stopResize = () => {
        dragState.current = null;
        setIsResizing(false);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", resize);
        window.removeEventListener("pointerup", stopResize);
        window.removeEventListener("pointercancel", stopResize);
      };

      window.addEventListener("pointermove", resize);
      window.addEventListener("pointerup", stopResize);
      window.addEventListener("pointercancel", stopResize);
    },
    [enabled, size],
  );

  return (
    <div className={cn("relative", className)}>
      {children}
      {enabled && (
        <button
          type="button"
          aria-label="Resize chessboard"
          className="absolute bottom-0 right-0 z-20 hidden w-8 outline-none group/resize top-6 cursor-nwse-resize rounded-r-md md:block"
          onPointerDown={startResize}
        >
          <span
            className={cn(
              "absolute bottom-[2px] right-0 flex h-6 w-6 items-center justify-center rounded-md text-gray-400 opacity-0 transition-[opacity,color] duration-200 group-hover/resize:text-gray-600 group-hover/resize:opacity-100 group-focus-visible/resize:text-gray-600 group-focus-visible/resize:opacity-100 group-active/resize:text-gray-600 group-active/resize:opacity-100",
              isResizing && "text-gray-600 opacity-100",
            )}
          >
            <FaUpRightAndDownLeftFromCenter className="h-[12px] w-[12px] rotate-90" />
          </span>
        </button>
      )}
    </div>
  );
}
